import { useEffect, useRef, useState, type ChangeEvent } from 'react';

import {
  registerEditorFlush,
  setEditorFlushDirty,
} from '@/lib/editor-flush-registry';

const DEBOUNCE_MS = 300;

type Props = {
  /** Block id — used as the flush-registry owner for Save/Discard flush. */
  ownerId: string;
  value: string;
  placeholder?: string;
  className?: string;
  /** Called at most once per settled value — after the debounce fires or on blur. */
  onCommit: (value: string) => void;
};

/**
 * Locally-controlled prefix `<input>` for container blocks. `prefix` feeds
 * the naming signature (see `NamingFieldKeys` in documentDataSlice), so
 * dispatching `updateNode` on every keystroke — as a plain Redux-controlled
 * input would — re-runs the naming-map effect on every character typed.
 * Instead: keep the draft local, flush to Redux 300ms after the user stops
 * typing, and flush immediately on blur.
 */
function ContainerPrefixInput({
  ownerId,
  value,
  placeholder,
  className,
  onCommit,
}: Props) {
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Last value actually sent to `onCommit` (or the initial prop) — used to
  // skip redundant dispatches when the settled value hasn't changed.
  const lastCommittedRef = useRef(value);
  const draftRef = useRef(draft);

  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  // Resync the local draft when the prop changes from outside this input —
  // e.g. discard-changes reverting to the server value, or a fresh hydrate.
  // Skipped while this input is focused so an in-flight edit is never
  // clobbered by an external update (mirrors EditableText's focus guard).
  useEffect(() => {
    if (document.activeElement === inputRef.current) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- external value resync
    setDraft(value);
    lastCommittedRef.current = value;
    setEditorFlushDirty(ownerId, false);
  }, [value, ownerId]);

  function commit(next: string): void {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (next === lastCommittedRef.current) {
      setEditorFlushDirty(ownerId, false);
      return;
    }
    lastCommittedRef.current = next;
    setEditorFlushDirty(ownerId, false);
    onCommit(next);
  }

  useEffect(() => {
    return registerEditorFlush(ownerId, {
      flush: async () => {
        commit(draftRef.current);
      },
      cancel: () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
        setDraft(lastCommittedRef.current);
        draftRef.current = lastCommittedRef.current;
        setEditorFlushDirty(ownerId, false);
      },
      isBusy: () =>
        timerRef.current !== null ||
        draftRef.current !== lastCommittedRef.current,
    });
  }, [ownerId, onCommit]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  function handleChange(e: ChangeEvent<HTMLInputElement>): void {
    const next = e.target.value;
    setDraft(next);
    draftRef.current = next;
    setEditorFlushDirty(ownerId, true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      commit(next);
    }, DEBOUNCE_MS);
  }

  function handleBlur(): void {
    commit(draft);
  }

  return (
    <input
      ref={inputRef}
      className={className}
      value={draft}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder={placeholder}
    />
  );
}

export { ContainerPrefixInput };
